import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import BackButton from '../components/BackButton';
import { useAuth } from '../context/AuthContext';
import profileAPI from '../services/profileApi';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileStats from '../components/profile/ProfileStats';
import ProfileInfoForm from '../components/profile/ProfileInfoForm';
import RecentActivity from '../components/profile/RecentActivity';
import SecurityCard from '../components/profile/SecurityCard';
import AchievementsCard from '../components/profile/AchievementsCard';

const ExportCard = () => {
  const [busy, setBusy] = useState(false);
  const exportCsv = async () => {
    setBusy(true);
    try {
      const blob = await profileAPI.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `phantom-trades-history-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Trade history downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="glow-card p-6 rounded-xl" data-testid="profile-export-card">
      <h3 className="text-xl font-bold mb-2">Export Data</h3>
      <p className="text-sm text-gray-400 mb-4">Download every binary and AI arbitrage trade as a CSV file.</p>
      <button type="button" onClick={exportCsv} disabled={busy} data-testid="profile-export-csv-btn" className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export trade history (CSV)
      </button>
    </div>
  );
};

const Skeleton = () => (
  <div className="space-y-6 animate-pulse" data-testid="profile-loading">
    <div className="h-56 rounded-2xl bg-purple-500/10" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-36 rounded-xl bg-purple-500/10" />)}</div>
  </div>
);

const Profile = () => {
  const { updateUser } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await profileAPI.overview());
    } catch {
      toast.error('Could not load profile');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (patch) => {
    setData((d) => ({ ...d, profile: { ...d.profile, ...patch } }));
    if (patch.name) updateUser({ name: patch.name });
  };

  const startEdit = () => {
    setEditing(true);
    requestAnimationFrame(() => document.querySelector('[data-testid="profile-info-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navbar isDashboard={true} />
      <MobileHeader isDashboard={true} />
      <div className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl" data-testid="profile-page">
          <BackButton label="Back" />
          {!data ? <Skeleton /> : (
            <>
              <ProfileHeader profile={data.profile} onEdit={startEdit} onAvatarChanged={load} />
              <ProfileStats stats={data.stats} achievementsTotal={data.achievements.length} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <ProfileInfoForm profile={data.profile} editing={editing} setEditing={setEditing} onSaved={onSaved} />
                  <RecentActivity items={data.recent_activity} />
                </div>
                <div className="space-y-6">
                  <SecurityCard security={data.security} />
                  <AchievementsCard achievements={data.achievements} />
                  <ExportCard />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
